import { ApiError } from '../services/api/base';
import { formatDateDisplay } from './dateUtils';
import { getEventYear } from '../services/EventUtils';
import { formatFullName } from './nameUtils';
import type { Person, Relationship } from '../types';
import { TOUCH_LAYOUT_QUERY } from '../hooks/useTouchLayout';

type TFn = (key: string, params?: Record<string, string>) => string;

/**
 * #1825 — een herhaalde POST (na een time-out) vond de persoon van de eerste poging, maar zijn
 * latere aanpassing kwam niet aan (dataSync.slaPersoonOp): de fout draagt die bestaande persoon.
 * Meldingen zeggen dan "{naam} is opgeslagen; de aanpassing niet", niet "niets opgeslagen".
 */
export const bestaandePersoon = (
  err: unknown
): (Person & { relationships?: Relationship[] }) | undefined =>
  (err as { bestaandePersoon?: Person & { relationships?: Relationship[] } } | null)
    ?.bestaandePersoon;

export type FoutContext = {
  soort: 'persoon' | 'relatie' | 'ouders' | 'koppelen' | 'gebeurtenis' | 'media';
  /** Waar de melding verschijnt, als dat de wat-nu verandert (de wizard heeft geen personenlijst). */
  scherm?: 'wizard';
  persoon?: string;
  anderePersoon?: string;
  /**
   * Het kind dat een derde biologische ouder zou krijgen (TOO_MANY_PARENTS), als de server het
   * niet noemt (`childId`) of het niet in `personen` staat (bv. de nieuwe persoon zelf);
   * anders `anderePersoon`.
   */
  kind?: string;
  /** #1825 — om de personen die de server noemt (childId, parentId) bij naam te noemen. */
  personen?: Pick<Person, 'id' | 'givenNames' | 'surnamePrefix' | 'surname' | 'gender'>[];
  /**
   * #1825 — op deze plek kan de gebruiker het soort ouderrelatie zelf kiezen (de familiesectie
   * van de editor): dan is "kies een adoptie-, stief- of pleegrelatie" de wat-nu. Elders
   * (koppelvenster, snelmenu, Ouders toevoegen) kan dat niet: dan de bestaande ouders nakijken.
   */
  subtypeKeuze?: boolean;
  /** Label van de knop waarmee de gebruiker het opnieuw probeert. */
  knop: string;
  /** Opnieuw proberen maakt geen dubbele gegevens. */
  herhaalbaar: boolean;
  /** Het gaat om verwijderen, niet om opslaan: andere zinnen. */
  actie?: 'verwijderen';
  /** Touch-indeling (#1825 deel 3): het wat-nu zegt "Tik" in plaats van "Klik". */
  touch?: boolean;
  /** #1841 — wie de gebeurtenis intussen weghaalde (laatste bewerker), als dat bekend is. */
  wie?: string;
};

/**
 * #1826/#1829 — een 409 op de PUT van een bestaande persoon is een versieconflict, tenzij de
 * server een regel noemt (SAME_GENDER_PARENT: het geslacht botst met een andere ouder).
 */
export const isVersieConflict = (err: unknown): boolean => {
  const e = err as { status?: number; message?: string; code?: string } | null;
  return (
    (e?.status === 409 || !!e?.message?.includes('CONFLICT')) &&
    !['SAME_GENDER_PARENT', 'TOO_MANY_PARENTS'].includes(e?.code ?? '')
  );
};

/** Oorzaak → sleutelstam; de teksten staan in nl.ts/en.ts als `foutUitleg_<stam>_waarom|watNu`. */
function oorzaak(err: unknown, soort: FoutContext['soort']): string {
  // #1826 — PUT /events met een verouderde expected_version: iemand anders wijzigde hem intussen.
  if (err instanceof ApiError && err.status === 409 && err.message === 'CONFLICT')
    return 'conflict';
  if (err instanceof ApiError) {
    // #1841 — een gebeurtenis die er niet meer is: een ander haalde hem intussen weg.
    if (err.status === 404 && soort === 'gebeurtenis') return 'gebeurtenisWeg';
    switch (err.code) {
      case 'REQUEST_TIMEOUT':
        return 'timeout';
      case 'TIER_UPGRADE_REQUIRED':
        return 'tier';
      case 'DUPLICATE_ENTRY':
        // Versieconflict op een PUT: iemand anders wijzigde de persoon intussen.
        if ((err.details as unknown as { conflict?: boolean } | undefined)?.conflict)
          return 'conflict';
        if (soort === 'relatie' || soort === 'koppelen') return 'dubbeleRelatie';
        if (soort === 'persoon' || soort === 'ouders') return 'dubbelePersoon';
        // #1207 (routes/events.js) — één vitale gebeurtenis per soort per persoon.
        return soort === 'gebeurtenis' ? 'dubbeleGebeurtenis' : 'anders';
      case 'CIRCULAR_RELATIONSHIP':
        return 'cirkel';
      case 'PERSON_NOT_IN_PROJECT':
        return 'persoonWeg';
      case 'SIBLING_WITHOUT_PARENTS':
        return 'zonderOuders';
      case 'TOO_MANY_PARENTS':
        return 'teveelOuders';
      case 'SAME_GENDER_PARENT':
        // #1829 — geweigerd omdat het geslacht van een bestaande ouder wijzigde.
        return err.personId ? 'geslachtOuder' : 'zelfdeGeslachtOuder';
      case 'VALIDATION_ERROR':
        return 'ongeldig';
    }
    if (err.status === 403) return 'geenRechten';
  } else if (err instanceof TypeError && /fetch|network|load failed/i.test(err.message)) {
    return 'netwerk';
  }
  return 'anders';
}

/**
 * #1825 — echte schermnamen (menu, tabbladen, knoppen) als parameter, zodat een tekst ze
 * nooit los van de interface kan noemen.
 */
/**
 * #1825 eindreview — geeft een aanroeper geen `touch` mee (wizard, ouders toevoegen,
 * snelbewerken), dan beslist het scherm: één plek, elke melding "Tik" op touch.
 */
const touchScherm = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.(TOUCH_LAYOUT_QUERY)?.matches;

export const uiLabels = (t: TFn, touch = touchScherm()): Record<string, string> => ({
  // Klik of tik: structureel via de teksten, niet via vervangen in vertaalde zinnen.
  actie: t(touch ? 'foutUitleg_actieTik' : 'foutUitleg_actieKlik'),
  actieLaag: t(touch ? 'foutUitleg_actieTikLaag' : 'foutUitleg_actieKlikLaag'),
  dossierBewerken: t('editDossierBtn'),
  tabGebeurtenissen: t('peTabGebeurtenissen'),
  instellingen: t('settings'),
  abonnement: t('subscription'),
  personen: t('people'),
  media: t('media'),
  support: t('support'),
  familie: t('familySection'),
  prullenbak: t('deletedItems'),
});

/** De eerste letter als hoofdletter (gedeeld; ook de archiefvondsten gebruiken hem). */
export const hoofdletter = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function beschrijfFout(
  err: unknown,
  ctx: FoutContext,
  t: TFn
): { waarom: string; watNu: string; stam: string } {
  const stam = oorzaak(err, ctx.soort);
  // De personen die de server noemt, bij naam (niet in de lijst = de nieuwe persoon zelf).
  const fout = err instanceof ApiError ? err : undefined;
  const zoek = (id?: string) => (id ? ctx.personen?.find((p) => p.id === id) : undefined);
  const naam = (p?: Pick<Person, 'givenNames' | 'surnamePrefix' | 'surname'>) =>
    p ? formatFullName(p.givenNames, p.surnamePrefix, p.surname) : '';
  const ouder = zoek(fout?.parentId);
  const params = {
    ...uiLabels(t, ctx.touch),
    persoon: ctx.persoon || t('foutUitleg_dezePersoon'),
    anderePersoon: ctx.anderePersoon || t('foutUitleg_dezePersoon'),
    kind: naam(zoek(fout?.childId)) || ctx.kind || ctx.anderePersoon || t('foutUitleg_dezePersoon'),
    // #1829 — wiens geslacht wijzigde (de server noemt hem; anders de persoon van de melding).
    ouderNieuw: naam(zoek(fout?.personId)) || ctx.persoon || t('foutUitleg_dezePersoon'),
    ouder: naam(ouder) || t('foutUitleg_eenAndereOuder'),
    ouderRol: t(
      ouder?.gender === 'M'
        ? 'foutUitleg_vader'
        : ouder?.gender === 'F'
          ? 'foutUitleg_moeder'
          : 'foutUitleg_ouderZelfdeGeslacht'
    ),
    knop: ctx.knop,
    wie: hoofdletter(ctx.wie || t('conflict1826_iemandAnders')),
  };
  // Na een time-out of zonder verbinding is de afloop onbekend: nogmaals klikken helpt alleen
  // als dat niets dubbel maakt; anders eerst kijken. Herhaalbaar verwijderen probeert opnieuw.
  const onzeker = stam === 'timeout' || stam === 'netwerk';
  const watNuStam =
    onzeker && !ctx.herhaalbaar
      ? ctx.actie === 'verwijderen'
        ? 'timeoutVerwijder'
        : 'timeoutControleer'
      : stam === 'timeout' && ctx.actie === 'verwijderen'
        ? 'timeoutVerwijderOpnieuw'
        : stam === 'dubbelePersoon' && ctx.scherm === 'wizard'
          ? 'dubbelePersoonWizard'
          : (stam === 'teveelOuders' || stam === 'zelfdeGeslachtOuder') && !ctx.subtypeKeuze
            ? `${stam}Open`
            : stam;
  return {
    waarom: t(`foutUitleg_${stam}_waarom`, params),
    watNu: t(`foutUitleg_${watNuStam}_watNu`, params),
    stam,
  };
}

/**
 * #1829 — de melding van een venster dat alles of niets opslaat (bulkbewerking, samenvoegen):
 * eerst wat er niet gebeurde (`kop`), dan waarom en wat nu. Nooit de ruwe servertekst.
 */
export function vensterMelding(
  err: unknown,
  ctx: { kop: string; knop: string; personen?: FoutContext['personen'] },
  t: TFn
): string {
  const { waarom, watNu } = beschrijfFout(
    err,
    { soort: 'persoon', knop: ctx.knop, herhaalbaar: true, personen: ctx.personen },
    t
  );
  return [ctx.kop, waarom, watNu].join(' ');
}

/** "{namen} is/zijn opgeslagen." (hoofdletter vooraan), of niets bij een lege lijst. */
export function opgeslagenZin(
  namen: string[],
  t: TFn,
  sleutel = 'opgeslagen',
  touch?: boolean
): string {
  if (!namen.length) return '';
  const tekst = t(`foutUitleg_${sleutel}${namen.length > 1 ? 'Meer' : 'Een'}`, {
    ...uiLabels(t, touch),
    namen: metEn(namen, t),
  });
  return hoofdletter(tekst);
}

/**
 * Versieconflict op de PUT van een bestaande persoon. Zijn er gebeurtenissen wél opgeslagen
 * of mislukt, dan staat dat er voor het conflict bij, met de eigen wijzigingen als niet opgeslagen.
 * Elke mislukking houdt zijn eigen oorzaak (een time-out blijft "niet zeker"); niet herhaalbaar,
 * want na een conflict lost nogmaals Opslaan niets op: eerst opnieuw openen.
 * `nietOpgeslagen`: een eigen zin over wat niet is opgeslagen ('' = niets te melden).
 * `venster`: de melding staat in een venster, niet in de editor (andere wat-nu).
 */
export function conflictMelding(
  args: {
    persoon: string;
    opgeslagen: string[];
    mislukt: Mislukking[];
    knop: string;
    nietOpgeslagen?: string;
    venster?: boolean;
    touch?: boolean;
    /** #1841 — wie een gebeurtenis weghaalde die mislukte (404). */
    wie?: string;
  },
  t: TFn
): string {
  const { persoon, opgeslagen, mislukt, knop, nietOpgeslagen, venster, touch, wie } = args;
  const conflict = venster
    ? `${t('foutUitleg_conflict_waarom')} ${t('foutUitleg_conflictVenster_watNu')}`
    : t('personConflict');
  if (!opgeslagen.length && !mislukt.length && nietOpgeslagen === undefined) return conflict;
  return [
    opgeslagenZin(opgeslagen, t, 'opgeslagen', touch),
    mislukt.length
      ? mislukkingenMelding({ mislukt, ctx: { persoon, knop, herhaalbaar: false, touch, wie } }, t)
      : '',
    nietOpgeslagen ?? t('foutUitleg_wijzigingenNietOpgeslagen', { persoon }),
    conflict,
  ]
    .filter(Boolean)
    .join(' ');
}

export function metEn(namen: string[], t: TFn): string {
  if (namen.length < 2) return namen.join('');
  return `${namen.slice(0, -1).join(', ')} ${t('foutUitleg_en')} ${namen[namen.length - 1]}`;
}

export function foutMelding(
  args: {
    err: unknown;
    ctx: FoutContext;
    opgeslagen?: string[];
    /** Verstuurd maar mislukt (na een time-out: onzeker). */
    nietOpgeslagen: string[];
    /** Nooit verstuurd, omdat een eerder deel al mislukte: zeker niet opgeslagen. */
    nietGeprobeerd?: string[];
  },
  t: TFn
): string {
  const { err, ctx, opgeslagen = [], nietOpgeslagen, nietGeprobeerd = [] } = args;
  const { waarom, watNu, stam } = beschrijfFout(err, ctx, t);
  // Bij koppelen betekent "bestaat al" dat de gewenste stand er al is: niets te
  // melden over (niet) opgeslagen.
  if (stam === 'dubbeleRelatie') return `${waarom} ${watNu}`;
  const weg = ctx.actie === 'verwijderen';
  return [
    opgeslagenZin(opgeslagen, t, weg ? 'verwijderd' : 'opgeslagen', ctx.touch),
    // Na een time-out weten we niet of de server het verstuurde deel toch opsloeg;
    // wat nooit verstuurd is, is zeker niet opgeslagen.
    ...(stam === 'timeout'
      ? [
          opgeslagenZin(nietOpgeslagen, t, weg ? 'verwijderOnzeker' : 'onzeker', ctx.touch),
          opgeslagenZin(nietGeprobeerd, t, weg ? 'nietVerwijderd' : 'nietOpgeslagen', ctx.touch),
        ]
      : [
          opgeslagenZin(
            [...nietOpgeslagen, ...nietGeprobeerd],
            t,
            weg ? 'nietVerwijderd' : 'nietOpgeslagen',
            ctx.touch
          ),
        ]),
    waarom,
    watNu,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * "{soortlabel} ({datum})" zoals het formulier ze toont (#1825). Attribuut- en overige
 * gebeurtenissen met een eigen titel: "Beroep — Bakker" (zoals EventRow). Een soort zonder
 * vertaling toont nooit de ruwe sleutel: de titel, anders "Gebeurtenis".
 */
export function gebeurtenisNaam(
  e: { type?: string; title?: string | null; date?: string },
  t: TFn,
  lang: string
): string {
  const vertaald = e.type ? t(e.type) : '';
  const bekend = !!vertaald && vertaald !== e.type;
  const titel = (e.title || '').trim();
  const label = bekend
    ? titel && titel !== vertaald
      ? `${vertaald} — ${titel}`
      : vertaald
    : titel || t('event');
  const datum = formatDateDisplay(
    e.date || getEventYear(e as { date?: string }) || undefined,
    lang
  );
  return datum ? `${label} (${datum})` : label;
}

/**
 * #1832 — de server weigerde een herstel (Prullenbak, ongedaan maken, versie terugzetten): de
 * persoon heeft intussen een actieve gebeurtenis van dezelfde soort (409 VITAL_EVENT_EXISTS).
 * "Jan Jansen heeft al een geboorte (2 maart 1851), dus er is niets teruggezet. …"
 * Elke andere fout: null, dan houdt de aanroeper zijn eigen melding.
 */
export function herstelGeweigerd(err: unknown, t: TFn, lang: string): string | null {
  if (!(err instanceof ApiError) || err.code !== 'VITAL_EVENT_EXISTS') return null;
  const { naam, soort = '', datum } = err.params ?? {};
  const wie = naam || t('foutUitleg_dezePersoon');
  const datumTekst = formatDateDisplay(datum || undefined, lang);
  const gebeurtenis = `${t(soort).toLocaleLowerCase(lang)}${datumTekst ? ` (${datumTekst})` : ''}`;
  return t('herstel1832_vitaalBestaat', {
    naam: wie.charAt(0).toLocaleUpperCase(lang) + wie.slice(1),
    gebeurtenis,
  });
}

export type Mislukking = { naam: string; err: unknown; soort: FoutContext['soort'] };

/**
 * Één melding voor deels mislukte opslag. Mislukkingen met dezelfde oorzaak vormen één blok
 * (namen + waarom + wat nu); verschillen de oorzaken, dan krijgt elk blok zijn eigen namen en
 * zijn eigen "wat nu". Zo zegt "klik nogmaals" alleen iets over wat een herhaling oplost.
 * Na een time-out is onzeker wat er is opgeslagen; de opgeslagen-zin staat alleen vooraan.
 */
export function mislukkingenMelding(
  args: {
    mislukt: Mislukking[];
    ctx: Omit<FoutContext, 'soort'>;
    opgeslagen?: string[];
    nietGeprobeerd?: string[];
  },
  t: TFn
): string {
  const { mislukt, ctx, opgeslagen = [], nietGeprobeerd = [] } = args;
  const groepen = new Map<string, Mislukking[]>();
  for (const m of mislukt) {
    const sleutel = oorzaak(m.err, m.soort);
    groepen.set(sleutel, [...(groepen.get(sleutel) ?? []), m]);
  }
  return [...groepen.values()]
    .map((groep, i) =>
      foutMelding(
        {
          err: groep[0].err,
          ctx: { ...ctx, soort: groep[0].soort },
          opgeslagen: i === 0 ? opgeslagen : [],
          nietOpgeslagen: groep.map((m) => m.naam),
          nietGeprobeerd: i === 0 ? nietGeprobeerd : [],
        },
        t
      )
    )
    .join(' ');
}
